import { PICK_FRAMES_PER_OP } from "../assets/budget.ts";
import { MAX_TILE_ID } from "../document/packed-tile.ts";
import { instanceFootprintProblem, plainInstanceFootprint } from "../document/footprint-cells.ts";
import type { EntityInstance, InstanceFootprint, JsonValue, LayerGroup, PropBehaviour, PropInstance, TileRef } from "../document/types.ts";
import { FACE_HEIGHT_MAX } from "../terrain/faces.ts";
import { OP_JSON_DEPTH_MAX, OP_NAME_MAX, OP_TEXT_MAX, type EmptyLayer, type KeyChanges, type OpPoint, type TilesetTiles } from "./op-types.ts";
import { LIVE_COORD_MAX } from "./protocol-shared.ts";

/**
 * The readers of an operation's values (ADR-086 decision 19): each takes a value of any shape and returns it
 * rebuilt with exactly its fields, or undefined when it is not exactly one, a field is of another type or out
 * of its bounds. The bounds are the ones a saved document is read with (`serialise.ts`, `validate.ts`): ids by
 * the document id rule, tiles within the packed form, face heights within the terrain's range, numbers finite,
 * no `__proto__` key; plus the lengths and depths an operation is limited to. What depends on the document
 * (a tileset the world lists, a position on the tile's pixel lattice, a cell inside the grid) is the
 * resolver's to check.
 */

/**
 * A parsed value's fields.
 */
export type Fields = Readonly<Record<string, unknown>>;

/**
 * The document id rule, as the live channel's map ids follow it: letters, digits, dots, dashes and underscores.
 */
const DOCUMENT_ID = /^[A-Za-z0-9._-]{1,128}$/;

/**
 * An operation's or an edit group's id: a tab's tag, a dash and a counter from 1.
 */
const OP_ID = /^[a-z0-9]{1,16}-[1-9][0-9]{0,14}$/;

const FORBIDDEN_KEY = "__proto__";
const SIMPLE_LAYER_KINDS: readonly string[] = ["tile", "prop", "entity", "collision", "zone", "door"];

/**
 * Reads a value as a plain record.
 *
 * @param value The value.
 * @returns Its fields, or undefined for anything but an object literal.
 */
export function recordOf(value: unknown): Fields | undefined {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return undefined;
  }
  const prototype: unknown = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null ? (value as Fields) : undefined;
}

/**
 * Whether a record has exactly its required keys and no key beyond its optional ones.
 *
 * @param fields The record.
 * @param required Keys it must have.
 * @param optional Keys it may have.
 * @returns True when every required key is present and no other key but an optional one.
 */
export function hasOnly(fields: Fields, required: readonly string[], optional: readonly string[] = []): boolean {
  const own = Object.keys(fields);
  return required.every((key) => Object.hasOwn(fields, key)) && own.every((key) => required.includes(key) || optional.includes(key));
}

/**
 * Whether a value is an id by the document id rule: a map's, a layer's, a group's, a prop's or an entity's.
 *
 * @param value The value.
 * @returns True for a matching string.
 */
export function isId(value: unknown): value is string {
  return typeof value === "string" && DOCUMENT_ID.test(value);
}

/**
 * Whether a value is an operation's or an edit group's id.
 *
 * @param value The value.
 * @returns True for `<tag>-<n>`.
 */
export function isOpId(value: unknown): value is string {
  return typeof value === "string" && OP_ID.test(value);
}

/**
 * Whether a value is a reference within its length: a tileset's, a terrain's, a variant's or a type's id.
 *
 * @param value The value.
 * @param least The fewest code units it may hold: 1 for a reference that may not be blank.
 * @returns True for a string of `least` to `OP_TEXT_MAX` code units.
 */
export function isText(value: unknown, least = 0): value is string {
  return typeof value === "string" && value.length >= least && value.length <= OP_TEXT_MAX;
}

/**
 * Whether a value is a name within its length: a map's, a layer's, a group's or a terrain entry's.
 *
 * @param value The value.
 * @returns True for a string of at most `OP_NAME_MAX` code units.
 */
export function isName(value: unknown): value is string {
  return typeof value === "string" && value.length <= OP_NAME_MAX;
}

/**
 * Whether a value is a record key an operation may name.
 *
 * @param value The value.
 * @returns True for a string of 1 to `OP_TEXT_MAX` code units other than `__proto__`.
 */
export function isKey(value: unknown): value is string {
  return isText(value, 1) && value !== FORBIDDEN_KEY;
}

/**
 * Whether a value is an integer within an inclusive range.
 *
 * @param value The value.
 * @param least The least allowed.
 * @param most The most allowed.
 * @returns True for an integer from `least` to `most`.
 */
export function isIntegerIn(value: unknown, least: number, most: number): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= least && value <= most;
}

/**
 * Whether a value is a face height a terrain layer may carry.
 *
 * @param value The value.
 * @returns True for an integer from 1 to `FACE_HEIGHT_MAX`.
 */
export function isFaceHeight(value: unknown): value is number {
  return isIntegerIn(value, 1, FACE_HEIGHT_MAX);
}

/**
 * Whether a value is a coordinate within the live channel's bounds.
 *
 * @param value The value.
 * @returns True for a finite number within `LIVE_COORD_MAX` of zero.
 */
function isCoord(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && Math.abs(value) <= LIVE_COORD_MAX;
}

/**
 * Reads a point.
 *
 * @param value The value.
 * @returns The point, or undefined when it is not a pair of coordinates.
 */
export function pointOf(value: unknown): OpPoint | undefined {
  return Array.isArray(value) && value.length === 2 && isCoord(value[0]) && isCoord(value[1]) ? [value[0], value[1]] : undefined;
}

/**
 * Reads a JSON value, copying it.
 *
 * @param value The value.
 * @param depth The depth the value sits at: 2 for a value of a fields record.
 * @returns The copy, or undefined when it nests past `OP_JSON_DEPTH_MAX`, holds a number that is not finite, a
 *   key that is `__proto__`, blank or too long, or anything JSON does not hold.
 */
function jsonOf(value: unknown, depth: number): JsonValue | undefined {
  if (value === null || typeof value === "string" || typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }
  if (depth > OP_JSON_DEPTH_MAX) {
    return undefined;
  }
  if (Array.isArray(value)) {
    const items: JsonValue[] = [];
    for (const item of value) {
      const read = jsonOf(item, depth + 1);
      if (read === undefined) {
        return undefined;
      }
      items.push(read);
    }
    return items;
  }
  return jsonRecordOf(value, depth);
}

/**
 * Reads a record of JSON values, copying it: an entity's or a behaviour's fields, a map's properties, or the
 * keys an intent sets.
 *
 * @param value The value.
 * @param depth The depth the record sits at, 1 by default.
 * @returns The copy, or undefined when it is not a record of JSON values within the depth.
 */
export function jsonRecordOf(value: unknown, depth = 1): { readonly [key: string]: JsonValue } | undefined {
  const fields = recordOf(value);
  if (fields === undefined || depth > OP_JSON_DEPTH_MAX) {
    return undefined;
  }
  const out: Record<string, JsonValue> = {};
  for (const key of Object.keys(fields)) {
    const read = isKey(key) ? jsonOf(fields[key], depth + 1) : undefined;
    if (read === undefined) {
      return undefined;
    }
    out[key] = read;
  }
  return out;
}

/**
 * Reads the keys an intent sets and removes.
 *
 * @param fields The intent's fields, of which `set` and `remove` are read.
 * @returns The changes, empty when the intent names neither, or undefined when `set` is not a record with at
 *   least one key, `remove` is not a list of at least one distinct key, or a key is in both.
 */
export function keyChangesOf(fields: Fields): KeyChanges | undefined {
  const set = fields["set"] === undefined ? null : jsonRecordOf(fields["set"]);
  const remove = fields["remove"] === undefined ? null : distinctOf(fields["remove"], isKey);
  if (set === undefined || remove === undefined || (set !== null && Object.keys(set).length === 0) || (remove !== null && remove.length === 0)) {
    return undefined;
  }
  if (set !== null && remove !== null && remove.some((key) => Object.hasOwn(set, key))) {
    return undefined;
  }
  return { ...(set === null ? {} : { set }), ...(remove === null ? {} : { remove }) };
}

/**
 * Reads a list of distinct strings.
 *
 * @param value The value.
 * @param accepts Whether a string may be in the list.
 * @returns The list, or undefined when it is not a list, an item is refused or two are equal.
 */
export function distinctOf(value: unknown, accepts: (item: unknown) => item is string): readonly string[] | undefined {
  if (!Array.isArray(value) || !value.every(accepts)) {
    return undefined;
  }
  return new Set(value).size === value.length ? [...(value as string[])] : undefined;
}

/**
 * Reads a list of tiles by tileset, as a `changeTilesets` intent's `pull` and `drop` carry them: at least one
 * entry, each exactly an id of 1 to `OP_TEXT_MAX` code units and at least one whole number from 0 to
 * `MAX_TILE_ID`, ascending and distinct. A list of more than `PICK_FRAMES_PER_OP` entries, or an entry of more
 * tiles, is refused by its length before any item is read, since each entry names a tile and one intent
 * carries at most that many.
 *
 * @param value The value.
 * @returns The entries rebuilt with exactly their fields, or undefined when the value is not such a list.
 */
export function tilesetTilesOf(value: unknown): readonly TilesetTiles[] | undefined {
  if (!Array.isArray(value) || value.length === 0 || value.length > PICK_FRAMES_PER_OP) {
    return undefined;
  }
  const entries: TilesetTiles[] = [];
  for (const item of value as readonly unknown[]) {
    const fields = recordOf(item);
    if (fields === undefined || !hasOnly(fields, ["id", "tiles"]) || !isText(fields["id"], 1)) {
      return undefined;
    }
    const tiles = fields["tiles"];
    if (!Array.isArray(tiles) || tiles.length === 0 || tiles.length > PICK_FRAMES_PER_OP || !tiles.every((tile, i) => isIntegerIn(tile, 0, MAX_TILE_ID) && (i === 0 || tile > (tiles[i - 1] as number)))) {
      return undefined;
    }
    entries.push({ id: fields["id"], tiles: [...(tiles as readonly number[])] });
  }
  return entries;
}

/**
 * Reads a tile reference.
 *
 * @param value The value.
 * @param flips Whether the tile may carry `flipX` and `flipY`, as a tile cell does and a prop's frame does not.
 * @returns The reference, or undefined when a field is missing or out of range, a flag is anything but true,
 *   or it carries any other field.
 */
export function tileOf(value: unknown, flips: boolean): TileRef | undefined {
  const fields = recordOf(value);
  if (fields === undefined || !hasOnly(fields, ["tileset", "tile"], flips ? ["flipX", "flipY"] : []) || !isText(fields["tileset"], 1) || !isIntegerIn(fields["tile"], 0, MAX_TILE_ID)) {
    return undefined;
  }
  const { flipX, flipY } = fields;
  if ((flipX !== undefined && flipX !== true) || (flipY !== undefined && flipY !== true)) {
    return undefined;
  }
  return { tileset: fields["tileset"], tile: fields["tile"], ...(flipX === true ? { flipX } : {}), ...(flipY === true ? { flipY } : {}) };
}

/**
 * Reads tile references keyed by name: a behaviour's state frames or its animations.
 *
 * @param value The value.
 * @returns The references, or undefined when the value is not a record of unflipped tiles under allowed keys.
 */
function tilesOf(value: unknown): { readonly [key: string]: TileRef } | undefined {
  const fields = recordOf(value);
  if (fields === undefined) {
    return undefined;
  }
  const out: Record<string, TileRef> = {};
  for (const key of Object.keys(fields)) {
    const tile = isKey(key) ? tileOf(fields[key], false) : undefined;
    if (tile === undefined) {
      return undefined;
    }
    out[key] = tile;
  }
  return out;
}

/**
 * Reads a prop's behaviour.
 *
 * @param value The value.
 * @returns The behaviour, or undefined when it is not exactly one.
 */
export function behaviourOf(value: unknown): PropBehaviour | undefined {
  const fields = recordOf(value);
  if (fields === undefined || !hasOnly(fields, ["type", "fields"], ["frames", "animations", "activation"]) || !isText(fields["type"])) {
    return undefined;
  }
  const own = jsonRecordOf(fields["fields"]);
  const frames = fields["frames"] === undefined ? null : tilesOf(fields["frames"]);
  const animations = fields["animations"] === undefined ? null : tilesOf(fields["animations"]);
  const activation = fields["activation"] === undefined ? null : activationOf(fields["activation"]);
  if (own === undefined || frames === undefined || animations === undefined || activation === undefined) {
    return undefined;
  }
  return { type: fields["type"], fields: own, ...(frames === null ? {} : { frames }), ...(animations === null ? {} : { animations }), ...(activation === null ? {} : { activation }) };
}

/**
 * Reads a behaviour's activation cells.
 *
 * @param value The value.
 * @returns The cells, or undefined when one is not a pair of integers within the coordinate bounds.
 */
function activationOf(value: unknown): readonly (readonly [number, number])[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const cells: (readonly [number, number])[] = [];
  for (const cell of value as readonly unknown[]) {
    if (!Array.isArray(cell) || cell.length !== 2 || !isIntegerIn(cell[0], -LIVE_COORD_MAX, LIVE_COORD_MAX) || !isIntegerIn(cell[1], -LIVE_COORD_MAX, LIVE_COORD_MAX)) {
      return undefined;
    }
    cells.push([cell[0], cell[1]]);
  }
  return cells;
}

/**
 * Whether a value is a prop's sort offset, in whole pixels.
 *
 * @param value The value.
 * @returns True for an integer within the coordinate bounds.
 */
export function isSortPixels(value: unknown): value is number {
  return isIntegerIn(value, -LIVE_COORD_MAX, LIVE_COORD_MAX);
}

/**
 * Reads a prop's own footprint (ADR-040 amendment of 2026-10-04, ADR-132 decision 12): an object of `cells` and
 * optionally `behind` and `front`, within the bounds and in canonical order, by the check a command makes
 * (`instanceFootprintProblem`).
 *
 * @param value The value.
 * @returns The footprint, or undefined when it is not exactly one.
 */
export function footprintOf(value: unknown): InstanceFootprint | undefined {
  if (instanceFootprintProblem(value) !== null) {
    return undefined;
  }
  return plainInstanceFootprint(value as InstanceFootprint);
}

/**
 * Reads a prop. Its kept placement (ADR-040 amendment of 2026-10-04) travels as `keepPlacement: true` or not at
 * all, the one form the document holds, so a false or any other value is refused.
 *
 * @param value The value.
 * @returns The prop, or undefined when it is not exactly one.
 */
export function propOf(value: unknown): PropInstance | undefined {
  const fields = recordOf(value);
  if (fields === undefined || !hasOnly(fields, ["id", "frame", "x", "y", "flipX", "flipY", "sortPixels"], ["behaviour", "keepPlacement", "footprint"])) {
    return undefined;
  }
  const footprint = fields["footprint"] === undefined ? null : footprintOf(fields["footprint"]);
  if (footprint === undefined || (footprint !== null && fields["behaviour"] !== undefined)) {
    return undefined;
  }
  const frame = tileOf(fields["frame"], false);
  const at = pointOf([fields["x"], fields["y"]]);
  const behaviour = fields["behaviour"] === undefined ? null : behaviourOf(fields["behaviour"]);
  const { id, flipX, flipY, sortPixels, keepPlacement } = fields;
  if (!isId(id) || frame === undefined || at === undefined || typeof flipX !== "boolean" || typeof flipY !== "boolean" || !isSortPixels(sortPixels) || behaviour === undefined || (keepPlacement !== undefined && keepPlacement !== true)) {
    return undefined;
  }
  return { id, frame, x: at[0], y: at[1], flipX, flipY, sortPixels, ...(behaviour === null ? {} : { behaviour }), ...(keepPlacement === true ? { keepPlacement: true as const } : {}), ...(footprint === null ? {} : { footprint }) };
}

/**
 * Reads an entity.
 *
 * @param value The value.
 * @returns The entity, or undefined when it is not exactly one.
 */
export function entityOf(value: unknown): EntityInstance | undefined {
  const fields = recordOf(value);
  if (fields === undefined || !hasOnly(fields, ["id", "type", "x", "y", "fields"])) {
    return undefined;
  }
  const at = pointOf([fields["x"], fields["y"]]);
  const own = jsonRecordOf(fields["fields"]);
  const { id, type } = fields;
  return isId(id) && isText(type) && at !== undefined && own !== undefined ? { id, type, x: at[0], y: at[1], fields: own } : undefined;
}

/**
 * Reads a layer group.
 *
 * @param value The value.
 * @returns The group, or undefined when it is not exactly an id and a name.
 */
export function groupOf(value: unknown): LayerGroup | undefined {
  const fields = recordOf(value);
  return fields !== undefined && hasOnly(fields, ["id", "name"]) && isId(fields["id"]) && isName(fields["name"]) ? { id: fields["id"], name: fields["name"] } : undefined;
}

/**
 * Reads a layer without content.
 *
 * @param value The value.
 * @returns The layer, or undefined when it is not exactly one: a terrain layer with its terrain fields, any
 *   other kind with none, and no grid or item list on either.
 */
export function emptyLayerOf(value: unknown): EmptyLayer | undefined {
  const fields = recordOf(value);
  if (fields === undefined) {
    return undefined;
  }
  const { kind, id, name, group } = fields;
  if (!isId(id) || !isName(name) || (group !== undefined && !isId(group))) {
    return undefined;
  }
  const membership = group === undefined ? {} : { group };
  if (kind === "terrain") {
    const { autoName, terrain, variant, faceHeight } = fields;
    if (!hasOnly(fields, ["kind", "id", "name", "terrain", "variant"], ["autoName", "group", "faceHeight"]) || !isText(terrain, 1) || !isText(variant) || (autoName !== undefined && typeof autoName !== "boolean") || (faceHeight !== undefined && !isFaceHeight(faceHeight))) {
      return undefined;
    }
    return { kind, id, name, ...(autoName === undefined ? {} : { autoName }), ...membership, terrain, variant, ...(faceHeight === undefined ? {} : { faceHeight }) };
  }
  if (typeof kind !== "string" || !SIMPLE_LAYER_KINDS.includes(kind) || !hasOnly(fields, ["kind", "id", "name"], ["group"])) {
    return undefined;
  }
  return { kind: kind as Exclude<EmptyLayer["kind"], "terrain">, id, name, ...membership };
}

/**
 * Reads a list with a reader for its items.
 *
 * @param value The value.
 * @param read Reads one item.
 * @returns The items, or undefined when the value is not a list or an item is refused.
 */
export function listOf<T>(value: unknown, read: (item: unknown) => T | undefined): T[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const items: T[] = [];
  for (const item of value as readonly unknown[]) {
    const one = read(item);
    if (one === undefined) {
      return undefined;
    }
    items.push(one);
  }
  return items;
}
